/* eslint-disable camelcase */
import React from "react";
import { useSelector } from "react-redux";
import { object, array, bool, string } from "prop-types";
import get from "lodash/get";
import CollectionTitle from "../../atoms/CollectionTitle";
import { HeroImage } from "../../atoms/HeroImage";
import { getStoryHeadline, getStoryLabel, isExternalStory } from "../../utils/story";
import { PublishAndReadTime } from "../../atoms/PublishAndReadTime";
import { DfpComponent } from "../../ads/dfp-component";
import { OtherStory } from "./OtherStory";
import { Link } from "@quintype/components";
import { Label } from "../../atoms/Label";
import Headline from "../../atoms/Headline";
import SubHeadline from "../../atoms/SubHeadline";
import { RowSeparator } from "../../atoms/RowSeparator";

import "./three-col-seven-stories.m.css";

const HeroStory = ({ story, showSubheadline, showReadTime, showTimestamp, showSeparator, sectionSlug }) => {
  const headline = getStoryHeadline(story);
  const label = getStoryLabel(story);
  const externalStoryUrl = isExternalStory(story);
  const href = externalStoryUrl || `/${story?.slug}`;
  const target = externalStoryUrl ? "_blank" : "_self";
  return (
    <Link href={href} target={target}>
      <div styleName="main-story" style={{ borderBottom: `${showSeparator || "none"}` }}>
        {label && <Label story={story} />}

        {headline && <Headline styleName="heading-one-story" headline={headline} sectionSlug={sectionSlug} />}
        {showSubheadline && story?.subheadline && (
          <SubHeadline styleName="subheadline" subHeadline={story?.subheadline} sectionSlug={sectionSlug} />
        )}
        <PublishAndReadTime story={story} showReadTime={showReadTime} showTimestamp={showTimestamp} />
        <HeroImage story={story} aspectRatio={[[4, 3]]} iconSizes={{ height: 48, width: 48 }} />
      </div>
    </Link>
  );
};

HeroStory.propTypes = {
  story: object,
  showSubheadline: bool,
  showReadTime: bool,
  showTimestamp: bool,
  showSeparator: bool,
  sectionSlug: string,
};

export const ThreeColSevenStories = ({ collection, stories, extraProp = "" }) => {
  if (stories.length < 1) {
    return null;
  }

  const {
    row_background_color,
    text_color,
    show_timestamp,
    show_read_time,
    show_subheadline,
    show_row_title,
    enable_sponsor_ad,
    sponsor_ad_details,
    show_column_separator,
    show_story_separator,
    show_row_separator,
    show_row_ad_unit_bottom,
  } = get(collection, ["associated-metadata"], {});
  const sponsorAdConfig = useSelector((state) => get(state, ["qt", "config", "ads-config", "slots", "sponsor_ad"], {}));
  const bottomAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_bottom_ad"], {})
  );
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));
  const sectionSlug = extraProp;
  return (
    <div className="row-component">
      <div
        style={{
          backgroundColor: row_background_color,
          paddingTop: row_background_color ? "15px" : "none",
        }}
      >
        <div
          styleName="stories-container"
          style={{
            color: text_color,
          }}
        >
          {show_row_title && (
            <CollectionTitle
              collection={collection}
              enableSponsorAd={enable_sponsor_ad}
              sponsorAdDetails={sponsor_ad_details}
              sponsorAdConfig={sponsorAdConfig}
              sectionSlug={sectionSlug}
            />
          )}
          <div styleName="columns">
            <div styleName="first-column" style={{ border: `${show_column_separator || "none"}` }}>
              <HeroStory
                story={stories[0]}
                showSubheadline={show_subheadline}
                showReadTime={show_read_time}
                showTimestamp={show_timestamp}
                showSeparator={show_story_separator}
                sectionSlug={sectionSlug}
              />
            </div>
            {stories.length > 1 && (
              <div styleName="second-column" style={{ border: `${show_column_separator || "none"}` }}>
                {stories.slice(1, 4).map((story) => (
                  <OtherStory
                    key={story.id}
                    story={story}
                    showSeparator={show_story_separator}
                    defaultFallback={true}
                    showTimestamp={show_timestamp}
                    showReadTime={show_read_time}
                    showSubheadline={show_subheadline}
                    sectionSlug={sectionSlug}
                  />
                ))}
              </div>
            )}
            {stories.length > 4 && (
              <div styleName="third-column" style={{ border: `${show_column_separator || "none"}` }}>
                {stories.slice(4, 7).map((story) => (
                  <OtherStory
                    key={story.id}
                    story={story}
                    showSeparator={show_story_separator}
                    defaultFallback={true}
                    showTimestamp={show_timestamp}
                    showReadTime={show_read_time}
                    showSubheadline={show_subheadline}
                    sectionSlug={sectionSlug}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {show_row_separator && <RowSeparator />}
      {show_row_ad_unit_bottom && (
        <DfpComponent
          adStyleName="ad-slot-size-300x250"
          id={`${pageType}-${collection.slug}-row-bottom-ad`}
          path={show_row_ad_unit_bottom}
          size={bottomAdConfig.sizes}
          viewPortSizeMapping={bottomAdConfig.view_port_size_mapping}
        />
      )}
    </div>
  );
};

ThreeColSevenStories.propTypes = {
  collection: object,
  stories: array,
  extraProp: string,
};

ThreeColSevenStories.storyLimit = 7;
